import { useEffect, useMemo, useState } from "react";
import styled, { useTheme } from "styled-components";
import axios from "axios";
import {
  Download,
  Search,
  X,
  BookOpen,
  Sparkles,
  Layers,
  CheckCircle2,
  AlertCircle,
  Loader2,
} from "lucide-react";
import {
  Btn,
  Tag,
  TagDot,
  Scrim,
  Modal,
  Seg,
  Skeleton,
  Act,
  TblWrap,
  TblToolbar,
  RowIco,
  CellFlex,
  CellPrimary,
  CellSub,
} from "../ui/primitives";
import { getCache, setCache, clearCache } from "../ui/listCache";
import {
  buildCatalogCacheKey,
  catalogSourceLabel,
  isVisibleInInstructorCatalog,
  parseCatalogResponse,
} from "./catalogListApi";
import useDemoActionGate from "../hooks/useDemoActionGate";
import DemoLockedDialog from "../ui/DemoLockedDialog";

/* ---------- layout ---------- */
const PageShell = styled.div`
  color: ${({ theme }) => theme.text};
  font-family: ${({ theme }) => theme.fontBody};
  padding-bottom: 48px;
`;

const Hero = styled.div`
  margin: 0 0 ${({ theme }) => theme.gap};

  .eyebrow {
    margin: 0 0 6px;
    font-size: ${({ theme }) => theme.fsMicro};
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.textSubtle};
  }

  h2 {
    margin: 0;
    font-family: ${({ theme }) => theme.fontHead};
    font-size: 22px;
    font-weight: 750;
    letter-spacing: -0.02em;
  }

  p {
    margin: 6px 0 0;
    max-width: 52ch;
    color: ${({ theme }) => theme.textMuted};
    font-size: ${({ theme }) => theme.fsSm};
    line-height: 1.5;
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const CompactStat = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 96px;
  padding: 16px 18px;
  border-radius: ${({ theme }) => theme.rLg};
  border: ${({ theme }) => theme.cardBorder};
  background: ${({ theme }) => theme.surface};
  box-shadow: ${({ theme }) => theme.cardShadow};
`;

const CompactIco = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 11px;
  display: grid;
  place-items: center;
  flex: none;
  background: ${({ theme }) => theme.accentSoft};
  color: ${({ theme }) => theme.accentStrong};
  box-shadow: inset 0 0 0 1px color-mix(in oklch, ${({ theme }) => theme.accent} 22%, transparent);

  svg {
    width: 18px;
    height: 18px;
  }

  ${({ $warning, theme }) =>
    $warning &&
    `
    background: color-mix(in oklch, ${theme.warning} 14%, ${theme.surface});
    color: oklch(0.58 0.13 60);
    box-shadow: inset 0 0 0 1px color-mix(in oklch, ${theme.warning} 22%, transparent);
  `}
`;

const CompactBody = styled.div`
  flex: 1;
  min-width: 0;

  .label {
    font-size: ${({ theme }) => theme.fsMicro};
    font-weight: 650;
    color: ${({ theme }) => theme.textMuted};
    line-height: 1.2;
  }

  .value {
    margin-top: 4px;
    font-family: ${({ theme }) => theme.fontHead};
    font-size: 24px;
    font-weight: 760;
    letter-spacing: -0.02em;
    line-height: 1.15;
    color: ${({ theme }) => theme.text};

    small {
      font-size: 14px;
      color: ${({ theme }) => theme.textSubtle};
      font-weight: 600;
    }
  }

  .meta {
    margin-top: 4px;
    font-size: 12px;
    color: ${({ theme }) => theme.textSubtle};
    line-height: 1.25;
  }
`;

const CompactBar = styled.div`
  height: 5px;
  border-radius: 3px;
  margin-top: 8px;
  background: ${({ theme }) => theme.surface3};
  overflow: hidden;
  box-shadow: inset 0 0 0 1px rgba(17, 20, 38, 0.04);

  > i {
    display: block;
    height: 100%;
    border-radius: 3px;
    background: ${({ theme }) =>
      `linear-gradient(90deg, ${theme.accent}, ${theme.accentStrong})`};
  }
`;

const CatalogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${({ theme }) => theme.gap};
`;

const typeTone = (type) => {
  if (type === "Certification") return "accent";
  if (type === "University") return "blue";
  if (type === "K-12") return "green";
  return "gray";
};

const typeAccent = (type, theme) => {
  if (type === "Certification") return theme.accentStrong;
  if (type === "University") return theme.info;
  if (type === "K-12") return theme.success;
  return theme.textMuted;
};

const CatalogCard = styled.article`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: ${({ theme }) => theme.padCard};
  border-radius: ${({ theme }) => theme.rLg};
  border: ${({ theme }) => theme.cardBorder};
  background: ${({ theme }) => theme.surface};
  box-shadow: ${({ theme }) => theme.cardShadow};
  transition: transform 0.18s cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 0.18s ease, border-color 0.18s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: ${({ theme }) => `${theme.ring1}, ${theme.shadowLg}`};
    border-color: ${({ theme }) => theme.accentLine};
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
`;

const CardIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  flex: none;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
  box-shadow: inset 0 0 0 1px ${({ $line }) => $line};
`;

const CardTitle = styled.h3`
  margin: 0;
  font-family: ${({ theme }) => theme.fontHead};
  font-size: 17px;
  font-weight: 750;
  letter-spacing: -0.02em;
  line-height: 1.35;
  color: ${({ theme }) => theme.text};
`;

const MetaRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin: 18px 0;

  .meta-box {
    padding: 10px 12px;
    border-radius: ${({ theme }) => theme.rMd};
    background: ${({ theme }) => theme.surface2};
    border: 1px solid ${({ theme }) => theme.border};

    span {
      display: block;
      font-size: ${({ theme }) => theme.fsMicro};
      font-weight: 650;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: ${({ theme }) => theme.textSubtle};
      margin-bottom: 4px;
    }

    b {
      font-family: ${({ theme }) => theme.fontHead};
      font-size: 14px;
      font-weight: 700;
      color: ${({ theme }) => theme.text};
    }
  }
`;

const CardFoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.border};

  .foot-note {
    font-size: ${({ theme }) => theme.fsSm};
    color: ${({ theme }) => theme.textSubtle};
  }
`;

const EmptyState = styled.div`
  grid-column: 1 / -1;
  padding: 56px 24px;
  text-align: center;
  border-radius: ${({ theme }) => theme.rLg};
  border: 1px dashed ${({ theme }) => theme.borderStrong};
  background: ${({ theme }) => theme.surface2};

  h3 {
    margin: 14px 0 6px;
    font-family: ${({ theme }) => theme.fontHead};
    font-size: 16px;
  }

  p {
    margin: 0;
    color: ${({ theme }) => theme.textMuted};
    font-size: ${({ theme }) => theme.fsSm};
  }
`;

const Toast = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: ${({ theme }) => theme.gap};
  padding: 12px 14px;
  border-radius: ${({ theme }) => theme.rMd};
  font-size: ${({ theme }) => theme.fsSm};
  border: 1px solid
    ${({ $error, theme }) =>
      $error ? colorMix(theme.danger, theme.surface, 28) : theme.accentLine};
  background: ${({ $error, theme }) =>
    $error ? colorMix(theme.danger, theme.surface, 10) : theme.accentSoft};
  color: ${({ theme }) => theme.text};

  button {
    margin-left: auto;
    border: none;
    background: none;
    color: ${({ theme }) => theme.textMuted};
    cursor: pointer;
    padding: 4px;
  }
`;

function colorMix(color, surface, pct) {
  return `color-mix(in oklch, ${color} ${pct}%, ${surface})`;
}

const OptionCard = styled.button`
  width: 100%;
  text-align: left;
  padding: 14px 16px;
  margin-bottom: 10px;
  border-radius: ${({ theme }) => theme.rMd};
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
  border: 1px solid
    ${({ $active, theme }) => ($active ? theme.accent : theme.border)};
  background: ${({ $active, theme }) =>
    $active ? theme.accentSoft : theme.surface2};
  box-shadow: ${({ $active, theme }) =>
    $active ? `0 0 0 3px ${theme.accentSoft}` : "none"};

  strong {
    display: block;
    font-size: 14px;
    margin-bottom: 3px;
    color: ${({ theme }) => theme.text};
  }

  span {
    font-size: ${({ theme }) => theme.fsSm};
    color: ${({ theme }) => theme.textMuted};
    line-height: 1.45;
  }
`;

const PreviewCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  margin-bottom: 16px;
  border-radius: ${({ theme }) => theme.rMd};
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface2};
`;

/* Shown while the import runs — cloning every question/answer of a large
   course takes minutes, so set the expectation instead of looking hung. */
const ImportingNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: ${({ theme }) => theme.rMd};
  border: 1px solid ${({ theme }) => theme.accentLine};
  background: ${({ theme }) => theme.accentSoft};
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.text};
  line-height: 1.5;

  svg {
    flex: 0 0 auto;
    margin-top: 2px;
    color: ${({ theme }) => theme.accent};
    animation: catalogImportSpin 0.9s linear infinite;
  }

  @keyframes catalogImportSpin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const CatalogCardShimmer = styled(CatalogCard)`
  pointer-events: none;

  &:hover {
    transform: none;
    box-shadow: ${({ theme }) => theme.cardShadow};
    border-color: ${({ theme }) => theme.border};
  }
`;

const CompactStatShimmer = styled(CompactStat)`
  pointer-events: none;
`;

function StatCardSkeleton({ withBar = false }) {
  return (
    <CompactStatShimmer aria-hidden="true">
      <Skeleton $w="40px" $h="40px" $r="11px" />
      <CompactBody>
        <Skeleton $w="55%" $h="10px" $r="4px" $mb="8px" />
        <Skeleton $w="36%" $h="24px" $r="6px" $mb="8px" />
        {withBar ? (
          <Skeleton $w="100%" $h="5px" $r="3px" $mb="8px" />
        ) : null}
        <Skeleton $w="72%" $h="11px" $r="4px" />
      </CompactBody>
    </CompactStatShimmer>
  );
}

function CatalogCardSkeleton() {
  return (
    <CatalogCardShimmer aria-hidden="true">
      <CardTop>
        <Skeleton $w="48px" $h="48px" $r="14px" />
        <Skeleton $w="92px" $h="24px" $r="999px" />
      </CardTop>
      <Skeleton $w="78%" $h="18px" $r="8px" $mb="8px" />
      <Skeleton $w="48%" $h="12px" $r="6px" $mb="18px" />
      <MetaRow>
        <div className="meta-box">
          <Skeleton $w="62%" $h="10px" $r="4px" $mb="8px" />
          <Skeleton $w="76%" $h="14px" $r="6px" />
        </div>
        <div className="meta-box">
          <Skeleton $w="52%" $h="10px" $r="4px" $mb="8px" />
          <Skeleton $w="84%" $h="14px" $r="6px" />
        </div>
      </MetaRow>
      <CardFoot>
        <Skeleton $w="132px" $h="12px" $r="6px" />
        <Skeleton $w="92px" $h="32px" $r="10px" />
      </CardFoot>
    </CatalogCardShimmer>
  );
}

const TYPE_FILTERS = [
  { key: "all", label: "All" },
  { key: "K-12", label: "K-12" },
  { key: "Certification", label: "Certification" },
  { key: "University", label: "University" },
];

function CourseTypeIcon({ type }) {
  const theme = useTheme();
  const color = typeAccent(type, theme);
  const bg = colorMix(color, theme.surface, 14);
  const line = colorMix(color, "transparent", 22);
  return (
    <CardIcon $color={color} $bg={bg} $line={line}>
      <BookOpen size={22} strokeWidth={2} />
    </CardIcon>
  );
}

/* Instructor read-only catalog — browse shared courses and import into workspace. */
export default function InstructorCourseCatalog() {
  const theme = useTheme();
  const { guardAction, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  const apiUrl = process.env.REACT_APP_API_URL;
  const userId = localStorage.getItem("userId");

  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [courses, setCourses] = useState([]);
  const [totalCourses, setTotalCourses] = useState(0);
  const [usedCourses, setUsedCourses] = useState(0);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [importTarget, setImportTarget] = useState(null);
  const [importOption, setImportOption] = useState("ONLY_COURSES");
  const [toast, setToast] = useState(null);

  const getAuth = () => {
    const u = process.env.REACT_APP_API_USERNAME;
    const p = process.env.REACT_APP_API_PASSWORD;
    return `Basic ${btoa(`${u}:${p}`)}`;
  };

  const applyCatalogData = (data) => {
    setCourses(data.courses || []);
    setTotalCourses(data.totalCourses || 0);
    setUsedCourses(data.usedCourses || 0);
  };

  useEffect(() => {
    fetchCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Show a cached list immediately, then always refetch. Returning cache-only
   * hid courses an admin had just shared in the same session.
   */
  const fetchCourses = async () => {
    // Sent so the backend can apply per-organization catalog targeting. It validates
    // the value against this user's memberships and falls back to their default org.
    const orgId = localStorage.getItem("orgId");
    const cacheKey = buildCatalogCacheKey(userId, orgId);
    const cached = getCache(cacheKey);
    if (cached) {
      applyCatalogData(cached);
      setLoading(false);
    } else {
      setLoading(true);
    }

    try {
      const res = await axios.get(
        `${apiUrl}/api/getCourseForImportFunction/${userId}`,
        { params: { orgId }, headers: { Authorization: getAuth() } }
      );
      const parsed = parseCatalogResponse(res.data);
      applyCatalogData(parsed);
      setCache(cacheKey, parsed);
    } catch {
      if (!cached) {
        setToast({ error: true, msg: "Could not load catalog courses." });
      }
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (courses || []).filter((course) => {
      if (!isVisibleInInstructorCatalog(course)) return false;
      const matchesSearch =
        !q ||
        course.courseName?.toLowerCase().includes(q) ||
        course.courseType?.toLowerCase().includes(q);
      const matchesType =
        typeFilter === "all" || course.courseType === typeFilter;
      return matchesSearch && matchesType;
    });
  }, [courses, search, typeFilter]);

  // The backend emits totalCourses = -1 for an unlimited plan (e.g. an invited
  // instructor sharing the org's Organization-tier pool). Treat that as "no cap"
  // rather than doing numeric subtraction (which would read "0 / Plan limit reached").
  const unlimited = totalCourses < 0;
  const slotsLeft = unlimited ? Infinity : Math.max(0, totalCourses - usedCourses);
  const atLimit = !unlimited && slotsLeft <= 0;
  const usagePct = unlimited
    ? 0
    : totalCourses > 0
      ? Math.min(100, (usedCourses / totalCourses) * 100)
      : 0;

  const openImport = (course) => {
    if (!guardAction('action.register.importCourse')) return;
    if (atLimit) {
      setToast({
        error: true,
        msg: "Course limit reached. Upgrade your plan to import more courses.",
      });
      return;
    }
    setImportOption("ONLY_COURSES");
    setImportTarget(course);
  };

  const handleImport = async () => {
    if (!guardAction('action.register.configureImport')) return;
    if (!importTarget) return;
    setImporting(true);
    try {
      await axios.post(
        `${apiUrl}/api/Copy-PreDefine-courses`,
        {
          userId,
          courseIds: [importTarget.courseId],
          option: importOption,
          // Active org from the sidebar switcher — multi-org users import into
          // the org they are currently working in (backend falls back to the
          // default org when absent).
          orgId: localStorage.getItem("orgId"),
        },
        { headers: { Authorization: getAuth() } }
      );
      clearCache(buildCatalogCacheKey(userId, localStorage.getItem("orgId")));
      setImportTarget(null);
      setToast({ error: false, msg: "Course imported successfully." });
      fetchCourses();
    } catch {
      setToast({ error: true, msg: "Import failed. Please try again." });
    } finally {
      setImporting(false);
    }
  };

  return (
    <PageShell>
      <Hero>
        <div>
          <p className="eyebrow">Register courses</p>
          <h2>Course catalog</h2>
          <p>
            Browse StudYaK courses and courses your organization has shared,
            then import a copy into your workspace. Catalog entries are
            read-only — import adds a copy to your course list.
          </p>
        </div>
      </Hero>

      <StatsRow>
        {loading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton withBar />
          </>
        ) : (
          <>
            <CompactStat>
              <CompactIco>
                <Layers size={18} />
              </CompactIco>
              <CompactBody>
                <div className="label">In catalog</div>
                <div className="value tnum">{filtered.length}</div>
                <div className="meta">Shared courses available to import</div>
              </CompactBody>
            </CompactStat>
            <CompactStat>
              <CompactIco>
                <Sparkles size={18} />
              </CompactIco>
              <CompactBody>
                <div className="label">Import slots left</div>
                <div className="value tnum">{unlimited ? "∞" : slotsLeft}</div>
                <div className="meta">
                  {unlimited
                    ? "No course cap on your plan"
                    : atLimit
                      ? "Plan limit reached"
                      : "Remaining on your plan"}
                </div>
              </CompactBody>
            </CompactStat>
            <CompactStat>
              <CompactIco $warning={atLimit}>
                <CheckCircle2 size={18} />
              </CompactIco>
              <CompactBody>
                <div className="label">Workspace usage</div>
                <div className="value tnum">
                  {usedCourses}
                  <small> / {unlimited ? "∞" : totalCourses}</small>
                </div>
                <CompactBar>
                  <i style={{ width: `${usagePct}%` }} />
                </CompactBar>
                <div className="meta">Courses in your workspace</div>
              </CompactBody>
            </CompactStat>
          </>
        )}
      </StatsRow>

      {toast && (
        <Toast $error={toast.error}>
          {toast.error ? (
            <AlertCircle size={18} color={theme.danger} />
          ) : (
            <CheckCircle2 size={18} color={theme.success} />
          )}
          {toast.msg}
          <button type="button" onClick={() => setToast(null)} aria-label="Dismiss">
            <X size={16} />
          </button>
        </Toast>
      )}

      <TblWrap style={{ marginBottom: 18 }}>
        <TblToolbar>
          <div className="search">
            <Search size={16} />
            <input
              placeholder="Filter by name or category…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Seg>
            {TYPE_FILTERS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                className={typeFilter === key ? "is-on" : ""}
                onClick={() => setTypeFilter(key)}
              >
                {label}
              </button>
            ))}
          </Seg>
        </TblToolbar>
      </TblWrap>

      <CatalogGrid>
        {loading &&
          Array.from({ length: 6 }).map((_, i) => (
            <CatalogCardSkeleton key={i} />
          ))}

        {!loading && filtered.length === 0 && (
          <EmptyState>
            <BookOpen size={32} color={theme.textSubtle} />
            <h3>No courses found</h3>
            <p>Try a different search or filter, or check back later.</p>
          </EmptyState>
        )}

        {!loading &&
          filtered.map((course) => (
            <CatalogCard key={course.courseId}>
              <CardTop>
                <CourseTypeIcon type={course.courseType} />
                <Tag $tone={typeTone(course.courseType)}>
                  <TagDot />
                  {course.courseType || "Course"}
                </Tag>
              </CardTop>

              <CardTitle>{course.courseName}</CardTitle>

              <MetaRow>
                <div className="meta-box">
                  <span>Category</span>
                  <b>{course.courseType || "General"}</b>
                </div>
                <div className="meta-box">
                  <span>Source</span>
                  <b>{catalogSourceLabel(course.source)}</b>
                </div>
              </MetaRow>

              <CardFoot>
                <span className="foot-note">Read-only · one-click import</span>
                <Btn
                  $variant="subtle"
                  $sm
                  onClick={() => openImport(course)}
                  disabled={atLimit}
                >
                  <Download size={15} />
                  Import
                </Btn>
              </CardFoot>
            </CatalogCard>
          ))}
      </CatalogGrid>

      {importTarget && (
        <Scrim onClick={() => !importing && setImportTarget(null)}>
          <Modal
            style={{ maxWidth: 520 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal__head">
              <div>
                <h3>Import course</h3>
                <p>Add a copy to your workspace</p>
              </div>
              <Act
                className="x-btn"
                title="Close"
                onClick={() => !importing && setImportTarget(null)}
              >
                <X size={18} />
              </Act>
            </div>

            <div className="modal__body">
              <PreviewCard>
                <RowIco
                  style={{
                    background: colorMix(
                      typeAccent(importTarget.courseType, theme),
                      theme.surface,
                      16
                    ),
                    color: typeAccent(importTarget.courseType, theme),
                  }}
                >
                  {importTarget.courseName?.slice(0, 2).toUpperCase() || "CO"}
                </RowIco>
                <CellFlex style={{ minWidth: 0 }}>
                  <div>
                    <CellPrimary>{importTarget.courseName}</CellPrimary>
                    <CellSub>
                      {importTarget.courseType || "General"} · {catalogSourceLabel(importTarget.source)}
                    </CellSub>
                  </div>
                </CellFlex>
                <Tag $tone={typeTone(importTarget.courseType)}>
                  {importTarget.courseType || "Course"}
                </Tag>
              </PreviewCard>

              <OptionCard
                type="button"
                disabled={importing}
                $active={importOption === "ONLY_COURSES"}
                onClick={() => setImportOption("ONLY_COURSES")}
              >
                <strong>Course only</strong>
                <span>Content and materials — no trainings, no assessments</span>
              </OptionCard>
              <OptionCard
                type="button"
                disabled={importing}
                $active={importOption === "COURSES_WITH_TRAININGS"}
                onClick={() => setImportOption("COURSES_WITH_TRAININGS")}
              >
                <strong>Course + trainings</strong>
                <span>Content and materials plus video trainings with their questions</span>
              </OptionCard>
              <OptionCard
                type="button"
                disabled={importing}
                $active={importOption === "COURSES_WITH_ASSESSMENTS"}
                onClick={() => setImportOption("COURSES_WITH_ASSESSMENTS")}
              >
                <strong>Course + assessments</strong>
                <span>Content and materials plus quizzes, exams and evaluations</span>
              </OptionCard>
              <OptionCard
                type="button"
                disabled={importing}
                $active={importOption === "COURSES_WITH_ASSESSMENTS_AND_TRAININGS"}
                onClick={() => setImportOption("COURSES_WITH_ASSESSMENTS_AND_TRAININGS")}
              >
                <strong>Everything</strong>
                <span>Content, materials, trainings and all assessments</span>
              </OptionCard>

              {importing && (
                <ImportingNote role="status">
                  <Loader2 size={15} />
                  <span>
                    Importing your copy — every question, answer and training is
                    being cloned, so large courses can take a few minutes. Keep
                    this tab open; we&apos;ll confirm when it&apos;s done.
                  </span>
                </ImportingNote>
              )}
            </div>

            <div className="modal__foot">
              <Btn onClick={() => setImportTarget(null)} disabled={importing}>
                Cancel
              </Btn>
              <Btn $variant="primary" onClick={handleImport} disabled={importing}>
                <Download size={15} />
                {importing ? "Importing…" : "Import to my courses"}
              </Btn>
            </div>
          </Modal>
        </Scrim>
      )}
      <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </PageShell>
  );
}
